import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import Dashboard from "./pages/Dashboard";
import AchatsPage from "./pages/AchatsPage";
import AnalysePage from "./pages/AnalysePage";
import HomePage from "./pages/HomePage";
import FavorisPage from "./pages/FavorisPage";
import MasquesPage from "./pages/MasquesPage";
import ProduitsPage from "./pages/ProduitsPage";
import Scraping from "./pages/Scraping";
import StatutPage from "./pages/StatutPage";
import FournisseursPage from "./pages/FournisseursPage";
import AmazonInventory from "./pages/AmazonInventory";
import AmazonDashboard from "./pages/AmazonDashboard";
import AmazonAuth from "./pages/AmazonAuth";
import CSVViewer from "./pages/CSVViewer";
import FournisseursFilesPage from "./pages/FournisseursFilesPage";
import Jobs from "./pages/Jobs";
import CommandesPage from "./pages/CommandesPage";
import DepensesPage from "./pages/DepensesPage";
import Layout from "./components/Layout";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { NotificationProvider } from './context/NotificationContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthCallback from './pages/AuthCallback';
import AuthModal from './components/auth/AuthModal';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import RulesPage from './pages/RulesPage';
import SettingsAmazonCallback from './pages/SettingsAmazonCallback';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import Settings from './components/Settings';

// Thème personnalisé
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1F2C45",
      contrastText: "#fff"
    },
    secondary: {
      main: "#2563EB",
      contrastText: "#fff"
    },
    success: {
      main: "#28A745"
    },
    error: {
      main: "#E3342F"
    },
    background: {
      default: "#F4F6F8",
      paper: "#fff"
    },
    grey: {
      100: "#F4F6F8",
      200: "#DADDE1",
      500: "#6B7280"
    },
    text: {
      primary: "#1F2C45",
      secondary: "#6B7280"
    }
  },
  typography: {
    fontFamily: "'Inter', 'Poppins', 'Roboto', 'Arial', 'sans-serif'",
    fontVariantNumeric: 'tabular-nums',
    h1: { fontSize: 36, fontWeight: 700 },
    h2: { fontSize: 30, fontWeight: 700 },
    h3: { fontSize: 24, fontWeight: 700 },
    h4: { fontSize: 20, fontWeight: 600 },
    h5: { fontSize: 18, fontWeight: 600 },
    h6: { fontSize: 16, fontWeight: 600 },
    body1: { fontSize: 16 },
    body2: { fontSize: 14 }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: 16,
          padding: '8px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
        containedPrimary: {
          backgroundColor: '#2563EB',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#1D4ED8',
          },
        },
        outlinedPrimary: {
          backgroundColor: '#fff',
          color: '#2563EB',
          border: '1px solid #DADDE1',
          '&:hover': {
            backgroundColor: '#F4F6F8',
            borderColor: '#2563EB',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 2px 8px 0 rgba(31,44,69,0.06)',
          padding: 24,
          background: '#fff',
          color: '#1F2C45',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: 14,
        },
        colorSuccess: {
          backgroundColor: '#28A745',
          color: '#fff',
        },
        colorError: {
          backgroundColor: '#E3342F',
          color: '#fff',
        },
        colorDefault: {
          backgroundColor: '#F4F6F8',
          color: '#6B7280',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#2563EB',
          fontWeight: 500,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            color: '#1D4ED8',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#DADDE1',
        },
        head: {
          fontWeight: 700,
          color: '#1F2C45',
          fontSize: 16,
        },
        body: {
          fontSize: 15,
        },
      },
    },
  },
});

function AuthRedirectWrapper({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(location.search);
      const redirect = params.get('redirect') || '/dashboard';
      navigate(redirect, { replace: true });
    }
  }, [user, location, navigate]);

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <CssBaseline />
              <Routes>
                {/* Routes publiques */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route
                  path="/auth"
                  element={
                    <AuthRedirectWrapper>
                      <AuthModal isOpen={true} onClose={() => {}} />
                    </AuthRedirectWrapper>
                  }
                />
                
                {/* Routes protégées avec Layout */}
                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="scraping" element={<Scraping />} />
                  <Route path="achats" element={<AchatsPage />} />
                  <Route path="commandes" element={<CommandesPage />} />
                  <Route path="depenses" element={<DepensesPage />} />
                  <Route path="produits" element={<ProduitsPage />} />
                  <Route path="fournisseurs-files" element={<FournisseursFilesPage />} />
                  <Route path="favoris" element={<FavorisPage />} />
                  <Route path="statut" element={<StatutPage />} />
                  <Route path="fournisseurs" element={<FournisseursPage />} />
                  <Route path="masques" element={<MasquesPage />} />
                  <Route path="jobs" element={<Jobs />} />
                  <Route path="analyse" element={<AnalysePage />} />
                  <Route path="amazon-inventory" element={<AmazonInventory />} />
                  <Route path="amazon-dashboard" element={<AmazonDashboard />} />
                  <Route path="amazon-auth" element={<AmazonAuth />} />
                  <Route path="csv-viewer" element={<CSVViewer />} />
                  <Route path="rules" element={<RulesPage />} />
                  <Route path="settings" element={<Settings />} />
                </Route>

                {/* Route par défaut - redirige vers /auth */}
                <Route path="*" element={<Navigate to="/auth" replace />} />

                {/* Nouvelle route pour SettingsAmazonCallback */}
                <Route path="/settings/amazon-callback" element={<SettingsAmazonCallback />} />
              </Routes>
              <Toaster
                position="top-right"
                toastOptions={{
                  className: 'toast',
                  success: {
                    className: 'toast-success',
                    duration: 3000,
                  },
                  error: {
                    className: 'toast-error',
                    duration: 4000,
                  },
                }}
              />
            </LocalizationProvider>
          </ThemeProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
